declare global {
  interface Window {
    dev: () => boolean;
    hasRemote: () => boolean;
    hasMode: (mode) => boolean;
    mode: string;
    reviewModeEnabled: boolean;
    savedStore: any;
    locationHistory: string | null;
    persistHistory: boolean;
    sectionInView: {
      section: HTMLElement;
      id: string;
      inView: boolean;
      index: number;
    };
    IS_CHAMELEON_MODULE: boolean;
    chameleon: any;
    fonts: any;
    FontFace: any;
    isHosted: boolean;
    source: string;
    enableAnalytics: boolean;
    analyticsUrl: string;
    projectId: number | string;
    accountId: string;
    versionId: number | string;
  }
  interface Document {
    fonts: any;
  }
}

import './setup';

import 'assets/whoopsBrowserIncompatible.png';
import { ErrorBoundary } from 'components/errorHandler';
import ModuleImagesPreloader from 'components/lib/PreLoaders/ModuleImagesPreloader';
import Module from 'components/Module';
import { HostingProvider } from 'hosting/context';
//
import React, { Fragment as _, FC, PropsWithChildren, useState } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import 'regenerator-runtime/runtime.js';
import { addFont } from 'services/fontService';
import getModuleImages from 'services/getModuleImages';
import getStore from 'services/getStore';
import 'services/scorm/scormService';
import smoothscroll from 'smoothscroll-polyfill';
import 'styles/global';
import 'whatwg-fetch';

import { SCORM } from 'pipwerks-scorm-api-wrapper';

if (!('IntersectionObserver' in window)) {
  require('intersection-observer');
}

smoothscroll.polyfill();

window.hasRemote = () => (window as any).getParams().hasRemote;

window.dev = () => {
  try {
    return window.parent.IS_CHAMELEON_EDITOR;
  } catch (err) {
    //
  }

  return ['DEVELOPMENT', 'EDIT'].includes(window?.mode);
};

// disable all react-beautiful-dnd development warnings
window['__react-beautiful-dnd-disable-dev-warnings'] = true;

const store = getStore();

try {
  window.hasMode = mode => {
    const windowMode = window.mode || 'DEVELOPMENT';
    return mode === windowMode;
  };

  window.client = parent.client;
} catch (err) {
  // console.warn(err);
}

const loadTheme = store.subscribe(() => {
  const {
    backgroundColor = '',
    fontFamily = '',
    fontSizes,
    fontFamilies = [],
  } = store.getState().theme;

  document.body.style.background = backgroundColor;

  if (!!fontFamilies.length) {
    fontFamilies.forEach(f => {
      addFont(f);
    });
  }

  // just wanting to smash this until it displays for sure
  if (fontFamily) {
    const checkFontFamilyDisplays = setInterval(() => {
      if (document.body.style.fontFamily.includes(fontFamily)) {
        clearInterval(checkFontFamilyDisplays);
      }

      document.body.style.fontFamily = fontFamily;
    }, 0);
  }

  loadTheme.unsubscribe && loadTheme.unsubscribe();
});

const isHosted = (window as any).getParams().hosted;

const enableAnalytics = (window as any).getParams().enableAnalytics;
//
const ccURL = (window as any).chameleon.CHAMELEON_URL;
// const analyticsURL = ...

enum Version {
  OnePointTwo = '1.2',
  TwoThousandAndFour = '2004',
}

export type IsScorm = {
  isInitialized?: boolean;
  scormUserId?: string;
  scormSessionId?: string;
};

let isScorm: IsScorm = {};

if (!(window as any).hosted) {
  try {
    //
    SCORM.version = (window as any).scormVersion || '1.2';

    if (SCORM.init()) {
      isScorm.isInitialized = true;
    }
  } catch (err) {
    console.log(err);
  }
}

const HostingWrapper: FC = ({ children }: PropsWithChildren) => {
  const projectId = (window as any).getParams().projectId;
  const accountId = (window as any).getParams().accountId;
  const versionId = (window as any).getParams().versionId;
  const source = (window as any).getParams().source;
  return (
    <HostingProvider
      isScorm={isScorm}
      isHosted={isHosted}
      enableAnalytics={enableAnalytics}
      url={ccURL}
      source={source}
      projectId={projectId}
      accountId={accountId}
      versionId={versionId}
    >
      {children}
    </HostingProvider>
  );
};

const moduleImages = getModuleImages();

let hideModulePreloaderOverride = false;

const ModuleWithPreloader = () => {
  const [showModule, setShowModule] = useState(false);

  return (
    <_>
      {showModule && <Module isScorm={isScorm} isHosted={isHosted} />}
      <ModuleImagesPreloader
        images={moduleImages}
        onComplete={() => {
          setShowModule(true);
        }}
      />
    </_>
  );
};

ReactDOM.render(
  <_>
    <ErrorBoundary>
      <HostingWrapper>
        <Provider store={store}>
          {hideModulePreloaderOverride ? (
            <Module isScorm={isScorm} isHosted={isHosted} />
          ) : !window.dev() && store.getState()?.theme?.useImagePreloader ? (
            <ModuleWithPreloader />
          ) : (
            <Module isScorm={isScorm} isHosted={isHosted} />
          )}

          {/* make the preloader available for editing. but do not do any preloading */}
          {window.dev() && (
            <ModuleImagesPreloader
              images={[]}
              onComplete={() => {
                //
              }}
            />
          )}
        </Provider>
      </HostingWrapper>
    </ErrorBoundary>
  </_>,
  document.querySelector('#root')
);
